import { FilterTable } from "@/features/FilterTable/FilterTable";
import { Field } from "@/features/FilterTable/types";
import {
  dateSchema,
  parseToDateValue,
  preprocessTextAsyncAutocompleteField,
} from "@/features/FormCard/utils";
import { z } from "zod";
import { DEFAULT_CATEGORY_OPTIONS } from "./config";
import { DepField, makeFiltersBuilder } from "@/features/FilterTable/utils";
import { DEFAULT_ASSET_CLASS_OPTIONS } from "../A6StaticData/edit-form-conf";
import { createAssetClassFilterFields } from "@/features/FilterTable/assetClassFilterFields";
import { Tables } from "@/gen/tableNames/tables";
import { Clients } from "@/lib/clients";
import { useTranslation } from "react-i18next";

const filterA6TransactionsSchema = z.object({
  a6_portfolio_client_contains: z
    .preprocess(preprocessTextAsyncAutocompleteField, z.string())
    .optional()
    .nullable(),
  a6_portfolio_vehicle_contains: z
    .preprocess(preprocessTextAsyncAutocompleteField, z.string())
    .optional()
    .nullable(),
  a6_portfolio_account_name_contains: z
    .preprocess(preprocessTextAsyncAutocompleteField, z.string())
    .optional()
    .nullable(),
  a6_portfolio_name_contains: z
    .preprocess(preprocessTextAsyncAutocompleteField, z.string())
    .optional()
    .nullable(),
  a6_portfolio_bank_name_contains: z
    .preprocess(preprocessTextAsyncAutocompleteField, z.string())
    .optional()
    .nullable(),
  actif_contains: z
    .preprocess(preprocessTextAsyncAutocompleteField, z.string())
    .optional()
    .nullable(),
  a6_portfolio_contract_name_contains: z
    .preprocess(preprocessTextAsyncAutocompleteField, z.string())
    .optional()
    .nullable(),
  a6_portfolio_investment_type_contains: z
    .preprocess(preprocessTextAsyncAutocompleteField, z.string())
    .optional()
    .nullable(),
  asset_class_1_equals: z.string().optional(),
  asset_class_2_equals: z.string().optional(),
  category_contains: z.string().optional(),
  transfer_contains: z.string().optional(),
  fee_type_contains: z.string().optional(),
  description_contains: z.string().optional(),
  date_from: z
    .preprocess((val) => parseToDateValue(val as string), dateSchema)
    .optional(),
  date_to: z
    .preprocess((val) => parseToDateValue(val as string), dateSchema)
    .optional(),
  create_from: z
    .preprocess((val) => parseToDateValue(val as string), dateSchema)
    .optional(),
  create_to: z
    .preprocess((val) => parseToDateValue(val as string), dateSchema)
    .optional(),
  a6_static_datum_name_contains: z
    .preprocess(preprocessTextAsyncAutocompleteField, z.string())
    .optional()
    .nullable(),
});

const filterA6TransactionsFields = filterA6TransactionsSchema.keyof().enum;

export type FilterA6TransactionsSchema = z.infer<
  typeof filterA6TransactionsSchema
>;

const portfolioDepFields: readonly DepField<FilterA6TransactionsSchema>[] = [
  {
    name: filterA6TransactionsFields.a6_portfolio_bank_name_contains,
    key: "bank_name",
    extract: "text",
    bucket: "equals",
  },
  {
    name: filterA6TransactionsFields.a6_portfolio_client_contains,
    key: "client",
    extract: "text",
    bucket: "equals",
  },
  {
    name: filterA6TransactionsFields.a6_portfolio_vehicle_contains,
    key: "vehicle",
    extract: "text",
    bucket: "equals",
  },
  {
    name: filterA6TransactionsFields.a6_portfolio_name_contains,
    key: "id",
    extract: "id",
    bucket: "equals",
  },
  {
    name: filterA6TransactionsFields.a6_portfolio_account_name_contains,
    key: "account_name",
    extract: "text",
    bucket: "equals",
  },
  {
    name: filterA6TransactionsFields.a6_portfolio_contract_name_contains,
    key: "contract_name",
    extract: "text",
    bucket: "contains",
  },
  {
    name: filterA6TransactionsFields.a6_portfolio_investment_type_contains,
    key: "investment_type",
    extract: "text",
    bucket: "equals",
  },
];

export function FilterA6Transactions() {
  const { t, i18n } = useTranslation("tables");
  const tableName = Tables.A6_TRANSACTIONS;

  const fields: Field[] = [
    {
      name: filterA6TransactionsFields.a6_portfolio_client_contains,
      label: t(`${tableName}.searchbox.a6_portfolio_client_contains`, { defaultValue: "Client" }),
      type: "asyncAutocomplete",
      base_url: `${Clients.A6}/${Tables.A6_PORTFOLIOS}`,
      column_name: "client",
      filters: makeFiltersBuilder<FilterA6TransactionsSchema>(
        portfolioDepFields,
        filterA6TransactionsFields.a6_portfolio_client_contains,
      ),
    },
    {
      name: filterA6TransactionsFields.a6_portfolio_vehicle_contains,
      label: t(`${tableName}.searchbox.a6_portfolio_vehicle_contains`, { defaultValue: "Vehicle / Beneficiary" }),
      type: "asyncAutocomplete",
      base_url: `${Clients.A6}/${Tables.A6_PORTFOLIOS}`,
      column_name: "vehicle",
      filters: makeFiltersBuilder<FilterA6TransactionsSchema>(
        portfolioDepFields,
        filterA6TransactionsFields.a6_portfolio_vehicle_contains,
      ),
    },
    {
      name: filterA6TransactionsFields.a6_portfolio_account_name_contains,
      label: t(`${tableName}.searchbox.a6_portfolio_account_name_contains`, { defaultValue: "Account group" }),
      type: "asyncAutocomplete",
      base_url: `${Clients.A6}/${Tables.A6_PORTFOLIOS}`,
      column_name: "account_name",
      filters: makeFiltersBuilder<FilterA6TransactionsSchema>(
        portfolioDepFields,
        filterA6TransactionsFields.a6_portfolio_account_name_contains,
      ),
    },
    {
      name: filterA6TransactionsFields.a6_portfolio_name_contains,
      label: t(`${tableName}.searchbox.a6_portfolio_name_contains`, { defaultValue: "Account" }),
      type: "asyncAutocomplete",
      base_url: `${Clients.A6}/${Tables.A6_PORTFOLIOS}`,
      column_name: "name",
      filters: makeFiltersBuilder<FilterA6TransactionsSchema>(
        portfolioDepFields,
        filterA6TransactionsFields.a6_portfolio_name_contains,
      ),
    },
    {
      name: filterA6TransactionsFields.a6_portfolio_contract_name_contains,
      label: t(`${Tables.A6_PORTFOLIOS}.columns.contract_name`),
      type: "asyncAutocomplete",
      base_url: `${Clients.A6}/${Tables.A6_PORTFOLIOS}`,
      column_name: "contract_name",
      filters: makeFiltersBuilder<FilterA6TransactionsSchema>(
        portfolioDepFields,
        filterA6TransactionsFields.a6_portfolio_contract_name_contains,
      ),
    },
    {
      name: filterA6TransactionsFields.a6_portfolio_investment_type_contains,
      label: t(`${Tables.A6_PORTFOLIOS}.columns.investment_type`),
      type: "asyncAutocomplete",
      base_url: `${Clients.A6}/${Tables.A6_PORTFOLIOS}`,
      column_name: "investment_type",
      filters: makeFiltersBuilder<FilterA6TransactionsSchema>(
        portfolioDepFields,
        filterA6TransactionsFields.a6_portfolio_investment_type_contains,
      ),
    },
    {
      name: filterA6TransactionsFields.a6_portfolio_bank_name_contains,
      label: t(`${tableName}.searchbox.a6_portfolio_bank_name_contains`, { defaultValue: "Bank" }),
      type: "asyncAutocomplete",
      base_url: `${Clients.A6}/${Tables.A6_PORTFOLIOS}`,
      column_name: "bank_name",
      filters: makeFiltersBuilder<FilterA6TransactionsSchema>(
        portfolioDepFields,
        filterA6TransactionsFields.a6_portfolio_bank_name_contains,
      ),
    },
    {
      name: filterA6TransactionsFields.actif_contains,
      label: t(`${tableName}.searchbox.actif_contains`, { defaultValue: "Asset" }),
      type: "asyncAutocomplete",
      base_url: `${Clients.A6}/${Tables.A6_STATIC_DATA}`,
      column_name: "name",
    },
    ...createAssetClassFilterFields({
      assetClassFieldName: filterA6TransactionsFields.asset_class_1_equals,
      assetSubclassFieldName: filterA6TransactionsFields.asset_class_2_equals,
      assetClassLabel: t(`${tableName}.searchbox.asset_class_1_equals`),
      assetSubclassLabel: t(`${tableName}.searchbox.asset_class_2_equals`),
      client: Clients.A6,
      staticDataTableName: Tables.A6_STATIC_DATA,
      language: "fr",
      assetSubclassOptions: DEFAULT_ASSET_CLASS_OPTIONS,
    }),
    {
      name: filterA6TransactionsFields.category_contains,
      label: t(`${tableName}.searchbox.category_contains`, { defaultValue: "Transaction type" }),
      type: "sectionSelect",
      useTranslation: true,
      section_name_translation_prefix: "transactions.category_groups",
      translate_key: "category",
      client: Clients.A6,
      table_name: Tables.A6_TRANSACTIONS,
      language: i18n.resolvedLanguage?.startsWith("fr") ? "fr" : "en",
      options: DEFAULT_CATEGORY_OPTIONS,
    },
    {
      name: filterA6TransactionsFields.transfer_contains,
      label: t(`${tableName}.searchbox.transfer_contains`, { defaultValue: "Transfer type" }),
      type: "select",
      useTranslation: true,
      client: Clients.A6,
      table_name: Tables.A6_TRANSACTIONS,
      language: i18n.resolvedLanguage?.startsWith("fr") ? "fr" : "en",
      translate_key: "transfer",
    },
    {
      name: filterA6TransactionsFields.fee_type_contains,
      label: t(`${tableName}.searchbox.fee_type_contains`, { defaultValue: "Fee type" }),
      type: "select",
      useTranslation: true,
      client: Clients.A6,
      table_name: Tables.A6_TRANSACTIONS,
      language: i18n.resolvedLanguage?.startsWith("fr") ? "fr" : "en",
      translate_key: "fee_type",
    },
    {
      name: filterA6TransactionsFields.description_contains,
      label: t(`${tableName}.searchbox.description_contains`, { defaultValue: "Description" }),
      type: "input",
    },
    {
      name: "date",
      type: "dateRange",
      label: t(`${tableName}.searchbox.date`, { defaultValue: "Value date" }),
      from: { name: filterA6TransactionsFields.date_from },
      to: { name: filterA6TransactionsFields.date_to },
      col_span: 2,
    },
    {
      name: "create",
      type: "dateRange",
      label: t(`${tableName}.searchbox.create`, { defaultValue: "Creation date" }),
      from: { name: filterA6TransactionsFields.create_from },
      to: { name: filterA6TransactionsFields.create_to },
      col_span: 2,
    },
  ];

  return (
    <FilterTable
      columns={2}
      client={Clients.A6}
      schema={filterA6TransactionsSchema}
      tableName={Tables.A6_TRANSACTIONS}
      fields={fields}
    />
  );
}
